import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { apartmentsApi, type Apartment, type ApartmentActivityHistoryItem, type ApartmentImportErrorSummary, type Building } from '../../api/apartmentsApi';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { APARTMENT_PERMISSIONS } from '../../constants/functionPermissions';
import ImportFileLink from '../../components/common/ImportFileLink';
import { BulkListPagination } from '../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../api/paginatedHistory';
import { apartmentMatchesSearchTerm } from '../../utils/apartmentCodeSearch';

type ApartmentListTab = 'LIST' | 'HISTORY';

const ApartmentList: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const { canCreate, canEdit, canDelete, canImport } = useCrudPermissions(APARTMENT_PERMISSIONS);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    importedCount: number;
    updatedCount?: number;
    renamedCount?: number;
    skippedCount?: number;
    skippedCodes?: string[];
    missingCount?: number;
    missingCodes?: string[];
    existingCount?: number;
    fileApartmentCount?: number;
  } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<ApartmentListTab>('LIST');
  const [activityHistory, setActivityHistory] = useState<ApartmentActivityHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [revertingBatchId, setRevertingBatchId] = useState<string | null>(null);
  const [selectedApartmentIds, setSelectedApartmentIds] = useState<string[]>([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [importFailure, setImportFailure] = useState<{
    errorCount?: number;
    errorSummary?: ApartmentImportErrorSummary[];
    missingCount?: number;
    missingCodes?: string[];
    existingCount?: number;
    fileApartmentCount?: number;
  } | null>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadApartments();
    } else {
      setApartments([]);
      setLoading(false);
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedBuildingId && activeTab === 'HISTORY') {
      loadActivityHistory();
    } else if (!selectedBuildingId) {
      setActivityHistory([]);
    }
  }, [selectedBuildingId, activeTab, historyPage]);

  useEffect(() => {
    setSelectedApartmentIds([]);
    setHistoryPage(1);
  }, [selectedBuildingId, searchTerm, activeTab]);

  const loadActivityHistory = async () => {
    if (!selectedBuildingId) return;
    try {
      setHistoryLoading(true);
      const result = await apartmentsApi.listApartmentActivityHistory(selectedBuildingId, {
        page: historyPage,
        limit: HISTORY_PAGE_SIZE,
      });
      setActivityHistory(result.data ?? []);
      setHistoryTotal(result.meta?.total ?? 0);
      setHistoryTotalPages(result.meta?.totalPages ?? 1);
    } catch (err) {
      console.error('Error loading apartment activity history:', err);
      setActivityHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (err) {
      console.error('Error loading buildings:', err);
      setError(t('failedToLoad'));
    }
  };

  const loadApartments = async () => {
    if (!selectedBuildingId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apartmentsApi.getApartments({ buildingId: selectedBuildingId });
      setApartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading apartments:', err);
      setError(t('errorLoadingApartments'));
      setApartments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('confirmDeleteApartment'))) {
      try {
        await apartmentsApi.deleteApartment(id);
        setApartments(prev => (prev || []).filter(apt => apt.id !== id));
        setSelectedApartmentIds(prev => prev.filter(aptId => aptId !== id));
        if (activeTab === 'HISTORY') {
          loadActivityHistory();
        }
      } catch (err: any) {
        console.error('Error deleting apartment:', err);
        const reasons = err.response?.data?.reasons;
        if (Array.isArray(reasons) && reasons.length > 0) {
          setError(`${t('errorDeletingApartment')}: ${reasons.join('; ')}`);
        } else {
          setError(err.response?.data?.message || t('errorDeletingApartment'));
        }
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedBuildingId || selectedApartmentIds.length === 0) {
      return;
    }

    const confirmMessage = t('confirmBulkDeleteApartments').replace(
      '{count}',
      selectedApartmentIds.length.toString(),
    );
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setBulkDeleteLoading(true);
      setError(null);
      const result = await apartmentsApi.bulkDeleteApartments(selectedBuildingId, selectedApartmentIds);
      const deletedIds = new Set(
        apartments
          .filter((apt) => result.deletedCodes.includes(apt.code))
          .map((apt) => apt.id),
      );
      setApartments((prev) => prev.filter((apt) => !deletedIds.has(apt.id)));
      setSelectedApartmentIds((prev) => prev.filter((id) => !deletedIds.has(id)));

      const summary = t('bulkDeleteResultSummary')
        .replace('{deleted}', result.deletedCount.toString())
        .replace('{skipped}', result.skipped.length.toString());
      if (result.skipped.length > 0) {
        const skippedDetails = result.skipped
          .slice(0, 5)
          .map((item) => `${item.code}: ${item.reasons.join(', ')}`)
          .join('; ');
        setError(`${summary} ${skippedDetails}`);
      }

      if (activeTab === 'HISTORY') {
        loadActivityHistory();
      }
    } catch (err: any) {
      console.error('Error bulk deleting apartments:', err);
      setError(err.response?.data?.message || t('errorDeletingApartment'));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const toggleApartmentSelection = (id: string) => {
    setSelectedApartmentIds((prev) =>
      prev.includes(id) ? prev.filter((aptId) => aptId !== id) : [...prev, id],
    );
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const updatedApartment = await apartmentsApi.toggleApartmentStatus(id);
      setApartments(prev => (prev || []).map(apt => 
        apt.id === id ? updatedApartment : apt
      ));
    } catch (err) {
      console.error('Error toggling apartment status:', err);
      setError(t('errorUpdatingApartment'));
    }
  };

  const handleBuildingChange = (buildingId: string) => {
    selectBuilding(user?.id, buildingId, setSelectedBuildingId);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleRecordsPerPageChange = (records: number) => {
    setRecordsPerPage(records);
    setCurrentPage(1);
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError('Please select a file to import');
      return;
    }

    if (!selectedBuildingId) {
      setImportError('Please select a building first');
      return;
    }

    try {
      setImportLoading(true);
      setImportError(null);
      setImportResult(null);
      setImportFailure(null);

      const result = await apartmentsApi.importApartments(selectedBuildingId, importFile);
      
      if (result.success) {
        setImportFailure(null);
        setImportResult({
          importedCount: result.importedCount ?? 0,
          updatedCount: result.updatedCount ?? 0,
          renamedCount: result.renamedCount ?? 0,
          skippedCount: result.skippedCount ?? 0,
          skippedCodes: result.skippedCodes,
          missingCount: result.missingCount ?? 0,
          missingCodes: result.missingCodes,
          existingCount: result.existingCount,
          fileApartmentCount: result.fileApartmentCount,
        });
        setImportFile(null);
        loadApartments();
        loadActivityHistory();
      } else {
        setImportFailure({
          errorCount: result.errorCount,
          errorSummary: result.errorSummary,
          missingCount: result.missingCount,
          missingCodes: result.missingCodes,
          existingCount: result.existingCount,
          fileApartmentCount: result.fileApartmentCount,
        });
        setImportError(result.message || t('importFailed'));
        loadActivityHistory();
      }
      
    } catch (err: any) {
      console.error('Error importing apartments:', err);
      setImportError(err.response?.data?.message || 'Failed to import apartments. Please check your file format.');
    } finally {
      setImportLoading(false);
    }
  };

  const downloadSampleFile = async () => {
    if (!selectedBuildingId) {
      setImportError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setImportError(null);
      await apartmentsApi.downloadApartmentImportSample(selectedBuildingId);
    } catch (err) {
      console.error('Error downloading apartment import sample:', err);
      setImportError(t('failedToExportSample'));
    }
  };

  const handleExport = async () => {
    if (!selectedBuildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setExporting(true);
      setError(null);
      await apartmentsApi.exportApartments(selectedBuildingId);
    } catch (err) {
      console.error('Error exporting apartments:', err);
      setError(t('failedToLoad'));
    } finally {
      setExporting(false);
    }
  };

  // Filter and paginate apartments
  const filteredApartments = (apartments || []).filter((apt) =>
    apartmentMatchesSearchTerm(
      {
        code: apt.code,
        name: apt.name,
        ownerName: apt.ownerName,
        usageTypeName: apt.usageType?.name,
        usageTypeCode: apt.usageType?.code,
      },
      searchTerm,
    ),
  );

  const totalRecords = filteredApartments.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentApartments = filteredApartments.slice(startIndex, endIndex);

  const toggleSelectAllOnPage = () => {
    const pageIds = currentApartments.map((apt) => apt.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedApartmentIds.includes(id));
    if (allSelected) {
      setSelectedApartmentIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedApartmentIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  const renderImportStatus = (status: ApartmentActivityHistoryItem['status']) => {
    if (status === 'FAILED') {
      return <span className="text-red-600">{t('importFailedStatus')}</span>;
    }
    if (status === 'PARTIAL') {
      return <span className="text-amber-600">{t('importPartialStatus')}</span>;
    }
    if (status === 'SUCCESS') {
      return <span className="text-green-600">{t('importSuccessStatus')}</span>;
    }
    return '—';
  };

  const renderActivityKind = (item: ApartmentActivityHistoryItem) => {
    switch (item.kind) {
      case 'IMPORT':
        return t('activityHistoryImport');
      case 'UPDATE':
        return t('activityHistoryUpdate');
      case 'DELETE':
        return t('activityHistoryDelete');
      case 'BULK_DELETE':
        return t('activityHistoryBulkDelete');
      default:
        return item.kind;
    }
  };

  const renderActivityDescription = (item: ApartmentActivityHistoryItem) => {
    if (item.kind === 'IMPORT') {
      const parts = [
        item.updatedCount ? t('apartmentImportSummary')
          .replace('{updated}', String(item.updatedCount ?? 0))
          .replace('{quantities}', String(item.quantityUpdateCount ?? 0)) : null,
        item.importedCount ? `${t('importNewCount')}: ${item.importedCount}` : null,
        item.missingCount ? `${t('importHistoryMissing')}: ${item.missingCount}` : null,
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(' · ') : '—';
    }
    if (item.apartmentCode) {
      return `${item.apartmentCode} — ${item.summary || ''}`;
    }
    return item.summary || '—';
  };

  const renderActivityChanges = (metadata?: Record<string, unknown> | null) => {
    const changes = metadata?.changes;
    if (!Array.isArray(changes) || changes.length === 0) {
      return null;
    }

    return (
      <div className="space-y-1">
        {changes.map((change, index) => {
          if (!change || typeof change !== 'object') {
            return null;
          }
          const label = 'label' in change ? String(change.label) : '';
          const oldValue = 'oldValue' in change ? String(change.oldValue) : '';
          const newValue = 'newValue' in change ? String(change.newValue) : '';
          return (
            <div key={`${label}-${index}`}>
              {label}: {oldValue} → {newValue}
            </div>
          );
        })}
      </div>
    );
  };

  const canRevertApartmentImport = (item: ApartmentActivityHistoryItem) => {
    if (item.kind !== 'IMPORT' || item.revertedAt || !item.hasAffectedQuantities) {
      return false;
    }
    if (user?.role === 'SUPERADMIN' || user?.role === 'OPERATOR') {
      return true;
    }
    return item.creator?.id === user?.id;
  };

  const handleRevertApartmentImport = async (item: ApartmentActivityHistoryItem) => {
    const fileLabel = item.fileName || 'import';
    const confirmed = window.confirm(
      t('apartmentImportRevertConfirm').replace('{fileName}', fileLabel),
    );
    if (!confirmed) {
      return;
    }

    try {
      setRevertingBatchId(item.id);
      const result = await apartmentsApi.revertApartmentImportBatch(item.id);
      alert(t('apartmentImportRevertSuccess').replace('{count}', String(result.restoredCount)));
      await loadActivityHistory();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('importFailed');
      alert(message);
    } finally {
      setRevertingBatchId(null);
    }
  };

  const renderBulkDeleteDetails = (metadata?: Record<string, unknown> | null) => {
    const deletedCodes = metadata && Array.isArray(metadata.deletedCodes)
      ? metadata.deletedCodes as string[]
      : [];
    const skipped = metadata && Array.isArray(metadata.skipped)
      ? metadata.skipped as Array<{ code?: string; reasons?: string[] }>
      : [];

    return (
      <div className="space-y-2">
        {deletedCodes.length > 0 && (
          <div>
            <strong>{t('activityHistoryDeletedCodes')}:</strong> {deletedCodes.slice(0, 50).join(', ')}
            {deletedCodes.length > 50 ? ` (+${deletedCodes.length - 50})` : ''}
          </div>
        )}
        {skipped.length > 0 && (
          <div className="space-y-1">
            <strong>{t('activityHistorySkippedApartments')}:</strong>
            {skipped.slice(0, 20).map((item) => (
              <div key={item.code || JSON.stringify(item)}>
                {item.code}: {(item.reasons || []).join(', ')}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderImportMissingSummary = (
    missingCount?: number,
    missingCodes?: string[],
    existingCount?: number,
    fileApartmentCount?: number,
  ) => (
    <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-600">{t('importExistingCount')}</div>
          <div className="text-lg font-bold text-gray-800">{existingCount ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600">{t('importFileApartmentCount')}</div>
          <div className="text-lg font-bold text-gray-800">{fileApartmentCount ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-gray-600">{t('importMissingCount')}</div>
          <div className="text-lg font-bold text-orange-700">{missingCount ?? 0}</div>
        </div>
      </div>
      {missingCount && missingCount > 0 && missingCodes && missingCodes.length > 0 && (
        <div className="text-xs text-orange-900">
          <strong>{t('importMissingCodes')}:</strong>{' '}
          {missingCodes.slice(0, 30).join(', ')}
          {missingCodes.length > 30 ? ` (+${missingCodes.length - 30})` : ''}
        </div>
      )}
    </div>
  );

  const renderImportErrorSummary = (errorSummary?: ApartmentImportErrorSummary[]) => {
    if (!errorSummary?.length) {
      return null;
    }

    return (
      <div className="space-y-2">
        <div className="text-sm font-semibold text-red-800">{t('importErrorSummary')}</div>
        {errorSummary.map((group) => (
          <div key={group.message} className="rounded border border-red-200 bg-white px-3 py-2 text-xs text-red-900">
            <div className="font-semibold">
              {group.count} {t('importErrorRows')}: {group.message}
            </div>
            {group.codes.length > 0 && (
              <div className="mt-1 text-gray-700">
                Mã căn: {group.codes.slice(0, 20).join(', ')}
                {group.codes.length > 20 ? ` (+${group.codes.length - 20})` : ''}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('apartmentList')}</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            disabled={exporting || !selectedBuildingId}
            className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            {exporting ? t('loading') : t('exportApartments')}
          </button>
          {canImport && (
            <button
              onClick={() => {
                setImportResult(null);
                setImportError(null);
                setImportFailure(null);
                setShowImportModal(true);
              }}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              <span>{t('importApartments')}</span>
            </button>
          )}
          {canCreate && (
            <Link
              to="/apartments/add"
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {t('addApartment')}
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Building Selection */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {shouldShowBuildingSelector(buildings) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('selectBuilding')}
              </label>
              <select
                value={selectedBuildingId}
                onChange={(e) => handleBuildingChange(e.target.value)}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('selectBuildingPlaceholder')}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedBuilding && (
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <div><span className="font-medium">{t('operator')}:</span> {selectedBuilding.operator?.name}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedBuildingId && (
        <div className="flex border-b border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('LIST')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'LIST'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('apartmentListTab')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('HISTORY')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'HISTORY'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('apartmentImportHistoryTab')}
          </button>
        </div>
      )}

      {activeTab === 'HISTORY' ? (
        !selectedBuildingId ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">{t('pleaseSelectBuildingToViewApartments')}</div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('activityHistoryKind')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryDate')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryStaff')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('activityHistoryDescription')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryStatus')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryFileName')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryDetails')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('apartmentImportRevert')}</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500">{t('loading')}</td>
                  </tr>
                ) : activityHistory.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-500">{t('importHistoryNoData')}</td>
                  </tr>
                ) : (
                  activityHistory.map((item) => {
                    const missingCodes = item.warnings?.missingCodes ?? [];
                    const skippedCodes = item.metadata && Array.isArray(item.metadata.skippedCodes)
                      ? item.metadata.skippedCodes as string[]
                      : [];
                    const isExpanded = expandedHistoryId === item.id;
                    const hasImportDetails =
                      item.kind === 'IMPORT'
                      && (missingCodes.length > 0 || skippedCodes.length > 0 || (item.errorCount ?? 0) > 0);
                    const hasActionDetails =
                      item.kind === 'UPDATE'
                      || item.kind === 'DELETE'
                      || item.kind === 'BULK_DELETE';

                    return (
                      <React.Fragment key={`${item.kind}-${item.id}`}>
                        <tr className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">{renderActivityKind(item)}</td>
                          <td className="px-4 py-3">{new Date(item.createdAt).toLocaleString('vi-VN')}</td>
                          <td className="px-4 py-3">{item.creator?.name || item.creator?.email || '—'}</td>
                          <td className="px-4 py-3">{renderActivityDescription(item)}</td>
                          <td className="px-4 py-3">
                            {item.kind === 'IMPORT' ? renderImportStatus(item.status) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {item.kind === 'IMPORT' ? (
                              <ImportFileLink fileName={item.fileName} fileUrl={item.fileUrl} />
                            ) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {(hasImportDetails || hasActionDetails) && (
                              <button
                                type="button"
                                onClick={() => setExpandedHistoryId(isExpanded ? null : item.id)}
                                className="text-blue-600 hover:underline"
                              >
                                {isExpanded ? t('close') : t('view')}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {item.kind === 'IMPORT' ? (
                              item.revertedAt ? (
                                <span
                                  className="text-gray-500 text-xs"
                                  title={`${t('apartmentImportReverted')} — ${item.revertRestoredCount ?? 0}`}
                                >
                                  {t('apartmentImportReverted')}
                                </span>
                              ) : canRevertApartmentImport(item) ? (
                                <button
                                  type="button"
                                  onClick={() => handleRevertApartmentImport(item)}
                                  disabled={revertingBatchId === item.id}
                                  className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                                >
                                  {revertingBatchId === item.id ? t('importLoading') : t('apartmentImportRevert')}
                                </button>
                              ) : item.quantityUpdateCount && item.quantityUpdateCount > 0 ? (
                                <span className="text-gray-400 text-xs" title={t('apartmentImportRevertUnavailable')}>
                                  —
                                </span>
                              ) : (
                                '—'
                              )
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-50">
                            <td colSpan={8} className="px-4 py-3 text-xs text-gray-700 space-y-3">
                              {item.kind === 'IMPORT' && (
                                <>
                                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                                    <div><span className="text-gray-600">{t('importHistoryTotalRows')}:</span> {item.totalRows ?? 0}</div>
                                    <div><span className="text-gray-600">{t('importNewCount')}:</span> {item.importedCount ?? 0}</div>
                                    <div><span className="text-gray-600">{t('importUpdatedCount')}:</span> {item.updatedCount ?? 0}</div>
                                    <div><span className="text-gray-600">{t('importRenamedCount')}:</span> {item.renamedCount ?? 0}</div>
                                    <div><span className="text-gray-600">{t('importSkippedCount')}:</span> {item.skippedCount ?? 0}</div>
                                  </div>
                                  {renderImportMissingSummary(
                                    item.missingCount,
                                    missingCodes,
                                    item.metadata?.existingCount as number | undefined,
                                    item.metadata?.fileApartmentCount as number | undefined,
                                  )}
                                  {renderImportErrorSummary(item.metadata?.errorSummary as ApartmentImportErrorSummary[] | undefined)}
                                  {item.errorCount && item.errorCount > 0 && Array.isArray(item.errors) && item.errors.length > 0 && !item.metadata?.errorSummary && (
                                    <div className="text-red-700">
                                      <strong>{t('importError')}:</strong> {item.errors.slice(0, 10).join('; ')}
                                    </div>
                                  )}
                                  {skippedCodes.length > 0 && (
                                    <div>
                                      <strong>{t('importSkippedCodes')}:</strong> {skippedCodes.join(', ')}
                                    </div>
                                  )}
                                </>
                              )}
                              {item.kind === 'UPDATE' && renderActivityChanges(item.metadata)}
                              {item.kind === 'BULK_DELETE' && renderBulkDeleteDetails(item.metadata)}
                              {item.kind === 'DELETE' && item.apartmentCode && (
                                <div>{item.apartmentCode}</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
            <BulkListPagination
              page={historyPage}
              totalPages={historyTotalPages}
              total={historyTotal}
              onPageChange={setHistoryPage}
              t={t}
            />
          </div>
        )
      ) : !selectedBuildingId ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('pleaseSelectBuildingToViewApartments')}</div>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">{t('loadingApartments')}</div>
        </div>
      ) : !apartments || apartments.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('noApartments')}</div>
          {canCreate && (
            <Link
              to="/apartments/add"
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-colors"
            >
              {t('addFirstApartment')}
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Search and Records Per Page */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('search')}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {canDelete && selectedApartmentIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {bulkDeleteLoading
                    ? t('loading')
                    : `${t('deleteSelectedApartments')} (${selectedApartmentIds.length})`}
                </button>
              )}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{t('recordsPerPage')}:</span>
                <select
                  value={recordsPerPage}
                  onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">{apartments.length}</div>
              <div className="text-gray-600">{t('totalApartments')}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-green-600">
                {apartments.filter(apt => apt.status === 'ACTIVE').length}
              </div>
              <div className="text-gray-600">{t('active')}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-red-600">
                {apartments.filter(apt => apt.status === 'INACTIVE').length}
              </div>
              <div className="text-gray-600">{t('inactive')}</div>
            </div>
          </div>

          {/* Pagination Info */}
          {totalRecords > 0 && (
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                {t('showingRecords').replace('{from}', (startIndex + 1).toString()).replace('{to}', Math.min(endIndex, totalRecords).toString()).replace('{total}', totalRecords.toString())}
              </div>
              <div className="text-sm text-gray-600">
                {t('page')} {currentPage} {t('of')} {totalPages}
              </div>
            </div>
          )}

          {/* Apartment Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={
                          currentApartments.length > 0
                          && currentApartments.every((apt) => selectedApartmentIds.includes(apt.id))
                        }
                        onChange={toggleSelectAllOnPage}
                        aria-label={t('selectAllOnPage')}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('stt')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('apartmentCode')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('apartmentName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('ownerName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('ownerPhone')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('usageType')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('apartmentGroup')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentApartments.map((apartment, index) => (
                    <tr key={apartment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedApartmentIds.includes(apartment.id)}
                          onChange={() => toggleApartmentSelection(apartment.id)}
                          aria-label={apartment.code}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/apartments/${apartment.id}/details`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
                          style={{ textTransform: 'none' }}
                        >
                          {apartment.code}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{apartment.name || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{apartment.ownerName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{apartment.ownerPhone || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{apartment.usageType?.name || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {apartment.group?.name || t('noApartmentGroup')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleStatus(apartment.id)}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full transition-colors ${
                            apartment.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {apartment.status === 'ACTIVE' ? t('active') : t('inactive')}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {canEdit && (
                          <Link
                            to={`/apartments/${apartment.id}/edit`}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            {t('edit')}
                          </Link>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(apartment.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            {t('delete')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('first')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('previous')}
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = currentPage - 2 + i;
                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('last')}
              </button>
            </div>
          )}

          {filteredApartments.length === 0 && searchTerm && (
            <div className="text-center py-8 text-gray-500">
              {t('noData')}
            </div>
          )}
        </>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header - Always visible */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h2 className="text-xl font-bold text-gray-800">{t('importApartments')}</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {!selectedBuildingId ? (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
                  Please select a building first before importing apartments.
                </div>
              ) : (
                <>
                  {/* Step 1: Download Sample */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step1DownloadSample')}</h3>
                    <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                      {t('sampleFileDescription')}
                    </p>
                    <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                      {t('importApartmentParkingQuantityHint')}
                    </p>
                    <button
                      onClick={downloadSampleFile}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>{t('downloadSampleFile')}</span>
                    </button>
                  </div>

                  {/* Step 2: Upload File */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step2UploadFile')}</h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="import-file"
                      />
                      <label
                        htmlFor="import-file"
                        className="cursor-pointer flex flex-col items-center space-y-2"
                      >
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        <span className="text-gray-600 text-sm">
                          {importFile ? importFile.name : t('clickToSelectFile')}
                        </span>
                        <span className="text-xs text-gray-500">
                          <span className="font-medium text-green-600">{t('excelRecommended')}</span> • {t('csvAlsoSupported')}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Required Fields */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-3">{t('requiredFields')}</h3>
                    <p className="text-gray-600 mb-3 text-sm">
                      {t('requiredFieldsDescription')}
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-red-600 text-xs mt-1">*</span>
                          <span><strong>code</strong> / <strong>MÃ CĂN</strong> - {t('fieldCode')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>name</strong> / <strong>TÊN CĂN HỘ</strong> - {t('fieldApartmentName')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>oldCode</strong> / <strong>MÃ CĂN CŨ</strong> - {t('fieldOldCode')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>apartmentGroup</strong> / <strong>NHÓM CĂN HỘ</strong> - {t('fieldApartmentGroup')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>usageType</strong> / <strong>SỬ DỤNG</strong> - {t('fieldUsageType')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>occupancyStatus</strong> / <strong>TRẠNG THÁI CĂN HỘ</strong> - {t('fieldOccupancyStatus')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>handoverDate</strong> / <strong>NGÀY BÀN GIAO</strong> - {t('fieldHandoverDate')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>peCode</strong> / <strong>PE CODE</strong> - {t('fieldPeCode')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>populationCount</strong> / <strong>SỐ NHÂN KHẨU</strong> - {t('fieldPopulationCount')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>ownerName</strong> / <strong>CHỦ SỞ HỮU</strong> - {t('fieldOwnerNameOptional')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>ownerPhone</strong> / <strong>SỐ ĐIỆN THOẠI</strong> - {t('fieldOwnerPhone')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>area</strong> / <strong>DIỆN TÍCH</strong> - {t('fieldArea')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>ownerEmail</strong> / <strong>EMAIL</strong> - {t('fieldOwnerEmail')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>invoiceEmail</strong> / <strong>EMAIL NHẬN HÓA ĐƠN</strong> - {t('fieldInvoiceEmail')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>address</strong> / <strong>ĐỊA CHỈ</strong> - {t('fieldAddress')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>taxCode</strong> / <strong>MÃ SỐ THUẾ</strong> - {t('fieldTaxCode')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>hasVatInvoice</strong> / <strong>CÓ HÓA ĐƠN VAT</strong> - {t('fieldHasVatInvoice')}</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <span className="font-medium text-gray-600 text-xs mt-1">•</span>
                          <span><strong>notes</strong> / <strong>GHI CHÚ</strong> - {t('fieldNotes')}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Messages */}
                  {importError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                      {importFailure ? t('importFailedSummary') : importError}
                    </div>
                  )}

                  {importFailure && (
                    <div className="bg-red-50 border border-red-300 text-red-900 px-4 py-4 rounded mb-4 text-sm space-y-3">
                      {renderImportMissingSummary(
                        importFailure.missingCount,
                        importFailure.missingCodes,
                        importFailure.existingCount,
                        importFailure.fileApartmentCount,
                      )}
                      {renderImportErrorSummary(importFailure.errorSummary)}
                    </div>
                  )}

                  {importResult && (
                    <div className={`px-4 py-4 rounded mb-4 text-sm border ${
                      (importResult.missingCount ?? 0) > 0
                        ? 'bg-amber-50 border-amber-300 text-amber-900'
                        : 'bg-green-50 border-green-300 text-green-800'
                    }`}>
                      <p className="font-semibold mb-3">{t('importSuccess')}</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        <div className="bg-white border border-green-200 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-bold text-green-600">{importResult.importedCount}</div>
                          <div className="text-xs text-gray-600 mt-1">{t('importNewCount')}</div>
                        </div>
                        <div className="bg-white border border-blue-200 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-bold text-blue-600">{importResult.updatedCount ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">{t('importUpdatedCount')}</div>
                        </div>
                        <div className="bg-white border border-purple-200 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-bold text-purple-600">{importResult.renamedCount ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">{t('importRenamedCount')}</div>
                        </div>
                        <div className="bg-white border border-amber-200 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-bold text-amber-600">{importResult.skippedCount ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">{t('importSkippedCount')}</div>
                        </div>
                        <div className="bg-white border border-orange-200 rounded-lg px-3 py-2 text-center">
                          <div className="text-2xl font-bold text-orange-600">{importResult.missingCount ?? 0}</div>
                          <div className="text-xs text-gray-600 mt-1">{t('importMissingCount')}</div>
                        </div>
                      </div>
                      {importResult.skippedCount && importResult.skippedCount > 0 && importResult.skippedCodes && importResult.skippedCodes.length > 0 && (
                        <p className="mt-3 text-xs text-amber-700">
                          {t('importSkippedCodes')}: {importResult.skippedCodes.slice(0, 10).join(', ')}
                          {importResult.skippedCodes.length > 10 ? ` (+${importResult.skippedCodes.length - 10})` : ''}
                        </p>
                      )}
                      {(importResult.missingCount ?? 0) > 0 && (
                        <div className="mt-3">
                          {renderImportMissingSummary(
                            importResult.missingCount,
                            importResult.missingCodes,
                            importResult.existingCount,
                            importResult.fileApartmentCount,
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer - Always visible with action buttons */}
            {selectedBuildingId && (
              <div className="border-t border-gray-200 bg-gray-50 p-4 rounded-b-lg">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || importLoading}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    {importLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{t('importLoading')}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        <span>{t('importApartments')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApartmentList; 